import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, BadgeCheck, Building2, Download } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SITE, IMAGES, CAPABILITIES_PDF } from "@/content/site";

const STANDARDS = [
  {
    num: "01",
    title: "CPO certified oversight",
    text: "Certified Pool Operator training is the recognized standard for commercial pool operation — and it's how every visit, reading, and adjustment is handled on your property.",
  },
  {
    num: "02",
    title: "Code-conscious chemistry",
    text: "Commercial water chemistry is managed with Florida's public pool requirements in mind — tighter targets, more frequent checks, and no guesswork under heavier bather loads.",
  },
  {
    num: "03",
    title: "Documentation for boards & managers",
    text: "Dated readings and service records you can hand to an HOA board, an ownership group, or a property manager — clear proof the pool is being run professionally.",
  },
  {
    num: "04",
    title: "Equipment that can't afford downtime",
    text: "A closed commercial pool means complaints, refunds, and liability. Routine equipment care and early problem detection keep high-demand systems running.",
  },
];

const PROPERTY_TYPES = [
  "Condominiums & HOAs",
  "Apartment Communities",
  "Hotels & Inns",
  "Community & Amenity Pools",
];

export const CommercialSection = () => (
  <section data-testid="commercial-section" className="py-16 sm:py-24 lg:py-32 bg-white border-y border-slate-200/70">
    <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <Reveal className="lg:col-span-7">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Commercial Pool Maintenance</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
            Commercial pools answer to a higher standard. So do we.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <p className="text-base text-slate-600 leading-relaxed">
            Shared and commercial pools carry heavier bather loads, tighter scrutiny, and real
            liability. Our commercial maintenance experience — backed by CPO certification — means
            your water, records, and equipment are managed like it.
          </p>
        </Reveal>
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-slate-200">
        {STANDARDS.map((s, i) => (
          <motion.div
            key={s.num}
            data-testid={`commercial-standard-${i}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.08 }}
            className="group py-8 sm:py-10 sm:px-7 border-b sm:border-b-0 sm:border-l border-slate-200 first:border-l-0"
          >
            <span className="font-mono text-sm text-pool transition-colors duration-300 group-hover:text-aqua">{s.num}</span>
            <h3 className="mt-3 font-display text-lg font-semibold tracking-tight text-navy leading-snug">{s.title}</h3>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed">{s.text}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-14 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 relative">
          <Reveal>
            <div className="clip-arch overflow-hidden ring-1 ring-slate-200 shadow-2xl shadow-navy/15">
              <img
                src={IMAGES.communityPool}
                alt="Condominium community pool and spa professionally maintained in Pinellas County Florida"
                className="w-full h-80 sm:h-[26rem] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="absolute -bottom-6 left-4 sm:left-8 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl shadow-navy/20 p-5 flex items-center gap-4"
            data-testid="commercial-cpo-card"
          >
            <span className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center shrink-0">
              <BadgeCheck className="w-6 h-6 text-sky2" />
            </span>
            <span>
              <span className="block font-display font-bold text-navy text-sm">CPO Certified</span>
              <span className="block text-xs text-slate-500 mt-0.5">Experienced in commercial property pool maintenance</span>
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-6">
          <Reveal>
            <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-navy">
              Who we keep the water clear for
            </h3>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Commercial pool care is a different discipline than residential service — the chemistry
              tolerances are tighter, the documentation matters more, and the cost of a bad week is
              measured in complaints, not just cloudy water. We bring that discipline to shared
              properties across Pinellas County.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-7 flex flex-wrap gap-2.5" data-testid="commercial-property-types">
              {PROPERTY_TYPES.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-2 bg-mist border border-slate-200 rounded-full px-4 py-2.5 text-sm font-semibold text-navy"
                >
                  <Building2 className="w-4 h-4 text-pool" /> {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-9 flex flex-col sm:flex-row gap-3.5">
              <Link
                to="/contact/"
                data-testid="commercial-quote-button"
                className="group inline-flex items-center justify-center gap-2.5 bg-navy text-white font-bold px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-pool hover:text-white hover:shadow-lg hover:shadow-cyan-500/25"
              >
                Request a Commercial Quote
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/commercial-pool-service/"
                data-testid="commercial-learn-more-link"
                className="group inline-flex items-center justify-center gap-2 font-display font-bold text-pool hover:text-ocean transition-colors px-2 py-3.5"
              >
                Explore commercial service
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={CAPABILITIES_PDF}
                download
                data-testid="commercial-section-pdf-link"
                className="group inline-flex items-center justify-center gap-2 font-display font-bold text-pool hover:text-ocean transition-colors px-2 py-3.5"
              >
                <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                Capabilities sheet (PDF)
              </a>
              <a
                href={SITE.phoneHref}
                data-testid="commercial-call-button"
                className="inline-flex items-center justify-center gap-2.5 border-2 border-navy text-navy font-bold px-7 py-3.5 rounded-full transition-all duration-300 hover:bg-navy hover:text-white"
              >
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
